import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";
import { z } from "zod";

import { GetCalendarSyncAuthUrlRequestSchema } from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import { Button, LoadingSpinner } from "~/components/design-system/index.js";

/** What the server folded into the OAuth `state` when the flow began. */
const OAuthStateSchema = z.object({
  userId: z.string().min(1),
  providerType: GetCalendarSyncAuthUrlRequestSchema.shape.providerType,
});

type OAuthState = z.infer<typeof OAuthStateSchema>;

function parseOAuthState(encoded: string | null): OAuthState | undefined {
  if (encoded === null) {
    return undefined;
  }
  try {
    const parsed = OAuthStateSchema.safeParse(JSON.parse(atob(encoded)));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

type Outcome =
  | { kind: "exchanging" }
  | { kind: "connected"; firstSyncFailed: boolean }
  | { kind: "notConnected" }
  | { kind: "failed" };

/**
 * Where a calendar provider sends the doctor back after the consent page.
 *
 * The tab this renders in was opened from the profile's Sync Calendar dialog,
 * so on success it exchanges the code and closes itself; the profile tab
 * refreshes when it regains focus. A tab the browser would not let script
 * close, or one the doctor reached some other way, is left showing the outcome
 * with a way back.
 */
export function CalendarCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read once: the URL is scrubbed below and must not be re-read after.
  const [request] = useState(() => ({
    code: searchParams.get("code"),
    state: parseOAuthState(searchParams.get("state")),
    providerError: searchParams.get("error") !== null,
  }));

  const [outcome, setOutcome] = useState<Outcome>(() => {
    if (request.providerError) {
      return { kind: "notConnected" };
    }
    return request.code && request.state
      ? { kind: "exchanging" }
      : { kind: "failed" };
  });

  // An authorisation code is single-use; it must not linger in the address
  // bar or history for a reload to replay.
  useEffect(() => {
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  // Strict mode runs effects twice; a second exchange of the same code fails.
  const exchangeStarted = useRef(false);

  useEffect(() => {
    const { code, state } = request;
    if (outcome.kind !== "exchanging" || !code || !state) {
      return;
    }
    if (exchangeStarted.current) {
      return;
    }
    exchangeStarted.current = true;

    void api.section12
      .exchangeCalendarSyncCode(state.userId, {
        providerType: state.providerType,
        code,
      })
      .then((response) => {
        setOutcome({
          kind: "connected",
          firstSyncFailed: response.data.status === "error",
        });
      })
      .catch(() => {
        setOutcome({ kind: "failed" });
      });
  }, [outcome.kind, request]);

  // Runs after the outcome has painted so a tab that refuses to close still
  // shows it.
  useEffect(() => {
    if (outcome.kind === "connected") {
      window.close();
    }
  }, [outcome.kind]);

  if (outcome.kind === "exchanging") {
    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
        data-testid="calendar-callback-status"
      >
        <LoadingSpinner />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Connecting your calendar...
        </Typography>
      </Box>
    );
  }

  const copy = match(outcome)
    .with({ kind: "connected", firstSyncFailed: true }, () => ({
      title: "Calendar connected",
      body: "Your calendar is linked, but the first sync failed. It will be retried automatically. You can close this tab.",
    }))
    .with({ kind: "connected" }, () => ({
      title: "Calendar connected",
      body: "Your availability now follows your calendar. You can close this tab.",
    }))
    .with({ kind: "notConnected" }, () => ({
      title: "Calendar not connected",
      body: "The connection was not completed on your calendar provider's page, so nothing has changed. You can close this tab and try again from your profile.",
    }))
    .with({ kind: "failed" }, () => ({
      title: "We couldn't connect your calendar",
      body: "Something went wrong while linking your calendar. Close this tab and try again from your profile.",
    }))
    .exhaustive();

  return (
    <Box
      sx={{
        maxWidth: 480,
        mx: "auto",
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        textAlign: "center",
      }}
      data-testid="calendar-callback-status"
      data-outcome={outcome.kind}
    >
      <Typography variant="h6">{copy.title}</Typography>
      <Typography variant="body1" sx={{ color: "text.secondary" }}>
        {copy.body}
      </Typography>
      <Button
        variant="outlined"
        label="Back to profile"
        onClick={() => {
          navigate(
            request.state
              ? routeFns.userProfilePage(request.state.userId)
              : "/",
          );
        }}
        testId="calendar-callback-back-btn"
      />
    </Box>
  );
}
